import { colorsPalettes } from "@similarweb/styles";
import { Button, EmptyState, ListContentLoader } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useGoogleAdsAccounts, type IGoogleAdsAccountOption } from "../hooks/use-google-ads-accounts";

interface IGoogleAdsConnectedStepProps {
    onSelectAccount: (account: IGoogleAdsAccountOption) => void;
}

export const GoogleAdsConnectedStepHeader = () => {
    return (
        <Text
            fontVariant="h5_2"
            color={colorsPalettes.carbon["500"]}
        >
            Choose a Google Ads account
        </Text>
    );
};

export const GoogleAdsConnectedStep = ({ onSelectAccount }: IGoogleAdsConnectedStepProps) => {
    const { data: accounts, isLoading, isError, error } = useGoogleAdsAccounts();

    return (
        <Stack
            gap="16px"
            data-automation="campaign-wizard-google-ads-connected"
        >
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon["400"]}
            >
                Your Google login is connected. Pick the Google Ads account to import a campaign from:
            </Text>

            {isLoading ? (
                <ListContentLoader loaderTitle="Loading your Google Ads accounts…" />
            ) : isError ? (
                <EmptyState
                    title="Couldn't load Google Ads accounts"
                    subtitle={error instanceof Error ? error.message : "Unknown error"}
                />
            ) : accounts && accounts.length > 0 ? (
                <Stack
                    gap="8px"
                    data-automation="campaign-wizard-google-ads-account-list"
                >
                    {accounts.map((account) => (
                        <Flex
                            key={account.id}
                            justifyContent="space-between"
                            alignItems="center"
                            padding="8px 12px"
                            background={colorsPalettes.carbon["50"]}
                            data-automation={`campaign-wizard-google-ads-account-${account.id}`}
                        >
                            <Stack gap="2px">
                                <Text
                                    fontVariant="subtitle2"
                                    color={colorsPalettes.carbon["500"]}
                                >
                                    {account.name}
                                </Text>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon["300"]}
                                >
                                    ID: {account.id}
                                </Text>
                            </Stack>
                            <Button
                                onClick={() => onSelectAccount(account)}
                                data-automation={`campaign-wizard-google-ads-account-select-${account.id}`}
                            >
                                Select
                            </Button>
                        </Flex>
                    ))}
                </Stack>
            ) : (
                <EmptyState
                    title="No Google Ads accounts found"
                    subtitle="This Google login has no selectable Google Ads accounts."
                />
            )}
        </Stack>
    );
};
